import { useMemo } from 'react';
import {
  getConfirmationDialogTypeFromKey,
  useEventConfirmationDialogSuppressions,
} from 'src/hooks/api/useEventConfirmationDialogSuppressions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature } from 'src/WebApiController';

import { AlertSuppressionSetting } from './AlertWithSuppressionDialog';

/**
 * Hook that returns the event mute timestamp for a specific alert type
 * @param key - The suppression key (e.g., 'suspiciousPriceSuppressionTimestamp')
 * @param viagogoEventId - Optional event ID for event-specific mutes
 * @returns The event mute timestamp if found, undefined otherwise
 */
export function useEventConfirmationDialogSuppressionTimestamp(
  key: keyof AlertSuppressionSetting,
  viagogoVirtualId?: string | null
): Date | undefined {
  const hasSellerEventAlertMutesFeature = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );

  const shouldQuery = hasSellerEventAlertMutesFeature && viagogoVirtualId;

  const { data: eventConfirmationDialogSuppressions } =
    useEventConfirmationDialogSuppressions(
      shouldQuery ? viagogoVirtualId : null
    );

  return useMemo(() => {
    if (!shouldQuery) {
      return undefined;
    }

    const confirmationDialogType = getConfirmationDialogTypeFromKey(key);
    if (!confirmationDialogType) {
      return undefined;
    }

    const suppression = eventConfirmationDialogSuppressions?.find(
      (m) => m.confirmationDialogType === confirmationDialogType
    );
    if (!suppression) {
      return undefined;
    }

    return new Date(suppression.endDate);
  }, [shouldQuery, key, eventConfirmationDialogSuppressions]);
}
